import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const AccountPayment = () => {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [partySummary, setPartySummary] = useState(null);

  // Form State
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [transactionType, setTransactionType] = useState('RECEIPT'); // RECEIPT or PAYMENT
  const [amount, setAmount] = useState('');
  const [payMode, setPayMode] = useState('Cash');
  const [payDetail, setPayDetail] = useState('');
  const [remark, setRemark] = useState('');

  // History State
  const [payments, setPayments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchAccounts = async () => {
    try {
      const res = await axiosClient.get('/accounts');
      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchPayments = async () => {
    setLoadingHistory(true);
    try {
      const res = await axiosClient.get(`/payments?accountId=${selectedAccountId}`);
      if (res.success) {
        setPayments(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchPayments();
  }, []);

  useEffect(() => {
    if (selectedAccountId) {
      const loadSummary = async () => {
        try {
          const res = await axiosClient.get(`/accounts/${selectedAccountId}`);
          if (res.success) {
            setPartySummary(res.data.financialSummary || null);
          }
        } catch (e) {
          console.error(e);
        }
      };
      loadSummary();
    } else {
      setPartySummary(null);
    }
    fetchPayments();
  }, [selectedAccountId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAccountId) {
      setError('Please select an Account/Party.');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setError('');
    setMessage('');
    setSaving(true);

    try {
      await axiosClient.post('/payments', {
        accountId: selectedAccountId,
        payDate,
        transactionType,
        amount: parseFloat(amount),
        payMode,
        payDetail,
        remark
      });

      const successTxt = `${transactionType === 'RECEIPT' ? 'Receipt' : 'Payment'} of ₹${parseFloat(amount).toFixed(2)} recorded successfully.`;
      setMessage(successTxt);
      toast.success(successTxt);
      setAmount('');
      setRemark('');
      setPayDetail('');

      // Refresh summary & table
      const res = await axiosClient.get(`/accounts/${selectedAccountId}`);
      if (res.success) setPartySummary(res.data.financialSummary || null);
      fetchPayments();
    } catch (err) {
      const errMsg = err.message || 'Error recording transaction.';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment record?')) {
      try {
        await axiosClient.delete(`/payments/${id}`);
        toast.success('Payment record deleted successfully.');
        fetchPayments();
        if (selectedAccountId) {
          const res = await axiosClient.get(`/accounts/${selectedAccountId}`);
          if (res.success) setPartySummary(res.data.financialSummary || null);
        }
      } catch (err) {
        toast.error(err.message || 'Error deleting payment.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-cash-coin text-primary me-2"></i> Account Payments & Receipts
          </h3>
          <p className="text-muted small mb-0">Record collections (Receipts) and supplier disbursements (Payments) with instant party balance updates</p>
        </div>
      </div>

      <div className="row g-4">
        {/* Entry Form & Party Summary */}
        <div className="col-12 col-lg-5">
          {/* Party Selector & Balance Card */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2 fw-semibold">
              <i className="bi bi-person-bounding-box text-primary me-2"></i> 1. Select Party / Account
            </div>
            <div className="card-body p-3">
              <label className="form-label small fw-semibold text-muted">Select Customer / Supplier *</label>
              <select
                className="form-select fw-bold mb-3"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                <option value="">-- Choose Party / Account --</option>
                {accounts.map((a) => (
                  <option key={a.Id} value={a.Id}>
                    {a.AccountName} ({a.AccountType})
                  </option>
                ))}
              </select>

              {partySummary && (
                <div className="p-3 bg-light rounded border">
                  <div className="row g-2 text-center small mb-2">
                    <div className="col-6">
                      <div className="text-muted">Total Sales</div>
                      <div className="fw-bold text-dark">₹{parseFloat(partySummary.totalSales || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Total Purchases</div>
                      <div className="fw-bold text-dark">₹{parseFloat(partySummary.totalPurchases || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Receipts Collected</div>
                      <div className="fw-bold text-success">₹{parseFloat(partySummary.totalReceipts || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Payments Made</div>
                      <div className="fw-bold text-danger">₹{parseFloat(partySummary.totalPayments || 0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between align-items-center">
                    <strong className="text-muted">Net Party Balance:</strong>
                    <span className={`fs-5 fw-bold ${partySummary.netBalance > 0 ? 'text-danger' : 'text-success'}`}>
                      {partySummary.netBalance > 0 ? `Receivable: ₹${parseFloat(partySummary.netBalance).toLocaleString('en-IN')}` : `Payable: ₹${Math.abs(partySummary.netBalance).toLocaleString('en-IN')}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Form */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light py-2 fw-semibold">
              <i className="bi bi-wallet2 text-primary me-2"></i> 2. Record Transaction
            </div>
            <div className="card-body p-3">
              {message && <div className="alert alert-success py-2 small">{message}</div>}
              {error && <div className="alert alert-danger py-2 small">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-muted">Transaction Type *</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${transactionType === 'RECEIPT' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setTransactionType('RECEIPT')}
                    >
                      <i className="bi bi-arrow-down-left-circle me-1"></i> Receipt (Money In)
                    </button>
                    <button
                      type="button"
                      className={`btn ${transactionType === 'PAYMENT' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setTransactionType('PAYMENT')}
                    >
                      <i className="bi bi-arrow-up-right-circle me-1"></i> Payment (Money Out)
                    </button>
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={payDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Amount (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control fw-bold text-primary"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Payment Mode</label>
                    <select
                      className="form-select"
                      value={payMode}
                      onChange={(e) => setPayMode(e.target.value)}
                    >
                      <option value="Cash">Cash</option>
                      <option value="UPI / GPay / PhonePe">UPI / Online</option>
                      <option value="Bank Transfer (NEFT/RTGS)">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Ref / Cheque #</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Txn ID / Chq No"
                      value={payDetail}
                      onChange={(e) => setPayDetail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold text-muted">Remark / Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Part payment for week sales..."
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-ocean w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                  disabled={saving || !selectedAccountId}
                >
                  {saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check-circle"></i>}
                  Record {transactionType === 'RECEIPT' ? 'Receipt' : 'Payment'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Transaction History Table */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0 text-dark">
                <i className="bi bi-clock-history text-primary me-2"></i> Recent Receipts & Payments History
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light small">
                    <tr>
                      <th>Date</th>
                      <th>Account Name</th>
                      <th>Type</th>
                      <th>Mode</th>
                      <th className="text-end">Amount (₹)</th>
                      <th>Remark</th>
                      <th className="text-center" style={{ width: '60px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingHistory ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                        </td>
                      </tr>
                    ) : payments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted small">
                          No transactions found.
                        </td>
                      </tr>
                    ) : (
                      payments.map((p) => (
                        <tr key={p.Id} className="small">
                          <td>{formatDate(p.PayDate)}</td>
                          <td className="fw-bold text-dark">{p.AccountName}</td>
                          <td>
                            <span
                              className={`badge ${
                                p.TransactionType === 'RECEIPT'
                                  ? 'bg-success'
                                  : p.TransactionType === 'PAYMENT'
                                  ? 'bg-danger'
                                  : p.TransactionType === 'SALE'
                                  ? 'bg-primary'
                                  : 'bg-warning text-dark'
                              }`}
                            >
                              {p.TransactionType}
                            </span>
                          </td>
                          <td>{p.paymode || 'Cash'}</td>
                          <td className="text-end fw-bold">
                            ₹{parseFloat(p.PaidAmt || p.TotalAmt || 0).toFixed(2)}
                          </td>
                          <td className="text-muted small">{p.Remark || '-'}</td>
                          <td className="text-center">
                            {p.TransactionType !== 'SALE' && p.TransactionType !== 'PURCHASE' && (
                              <button
                                className="btn btn-sm btn-outline-danger py-0 px-2"
                                onClick={() => handleDelete(p.Id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPayment;
